import {
  ReportItemGroup,
  ReportItemsDataRow,
  ReportItemsSection,
  ReportRowsBySource,
} from '../types';
import { GridRow } from '../../../types/types';
import { REPORT_MASTER_ROW_ID } from '../../../utils/dataset/reportRelations';

/** Backward-compatible read of the single-row and multi-row template shapes. */
export const resolveItemsDataRows = (
  section: ReportItemsSection
): ReportItemsDataRow[] =>
  Array.isArray(section.dataRows) && section.dataRows.length > 0
    ? section.dataRows
    : [
        {
          source: section.source,
          columns: section.columns,
          ...(section.repeatHeaderOnEachPage === undefined
            ? {}
            : { repeatHeaderOnEachPage: section.repeatHeaderOnEachPage }),
          ...(section.gridLines === undefined
            ? {}
            : { gridLines: section.gridLines }),
        },
      ];

const rowId = (row: GridRow, index: number): string =>
  String(
    row[REPORT_MASTER_ROW_ID] ?? row.id ?? row._id ?? `master-${index}`
  );

/**
 * Groups every subordinate dataset row under the master record used to fetch
 * it. Legacy/unmarked rows remain visible in the first group rather than being
 * silently discarded.
 */
export const buildReportItemGroups = (
  rowsBySource: ReportRowsBySource
): ReportItemGroup[] => {
  const masterRows = rowsBySource.master ?? [];
  if (masterRows.length === 0) return [];

  return masterRows.map((masterRow, masterIndex) => {
    const masterRowId = rowId(masterRow, masterIndex);
    const grouped: ReportRowsBySource = { master: [masterRow] };

    Object.entries(rowsBySource).forEach(([source, rows]) => {
      if (source === 'master') return;
      const markedRows = rows.filter(
        (row) => String(row[REPORT_MASTER_ROW_ID] ?? '') === masterRowId
      );
      const hasRelationMarkers = rows.some(
        (row) => row[REPORT_MASTER_ROW_ID] !== undefined
      );
      grouped[source] = hasRelationMarkers
        ? markedRows
        : masterIndex === 0
          ? rows
          : [];
    });

    return { masterRowId, rowsBySource: grouped };
  });
};
